import { useEffect, useId, useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { $countryProvincesStore, $countriesStore, $recipientCreateStore, $recipientMandatoryFieldsStore, $transferCurrenciesStore } from '@lib/store';
import { TransferRecipientTypeEnum } from '@shared/api';
import {
  AccountFormBlock,
  AddressFormBlock,
  CompanyFormBlock,
  ConfirmModal,
  type AccountFormData,
  type AddressFormData,
  type CompanyFormData,
} from '@widgets';
import { Button, Spinner } from '@shared/widgets';
import ConfirmIcon from '@assets/icons/confirm.svg';

type BusinessRecipientFormData = CompanyFormData & AddressFormData & AccountFormData;
const DEFAULT_VALUES: BusinessRecipientFormData = {
  companyName: '',
  email: '',
  address: '',
  postCode: '',
  city: '',
  province: '',
  companyCountry: '',
  iban: '',
  currency: '',
  bankName: '',
  bankCountry: '',
  accountNumber: '',
  bic: '',
  accountType: undefined,
  bankProvince: '',
  bankAddress: '',
  bankCity: '',
  branchCode: '',
};

export const BusinessRecipient = () => {
  const countriesStore = useUnit($countriesStore.store);
  const recipientMandatoryFieldsStore = useUnit($recipientMandatoryFieldsStore.store);
  const recipientStore = useUnit($recipientCreateStore.store);

  const formId = useId();
  const formControl = useForm<BusinessRecipientFormData>({ defaultValues: DEFAULT_VALUES });
  const bankCountry = formControl.watch('bankCountry');
  const companyCountry = formControl.watch('companyCountry');
  const currency = formControl.watch('currency');

  const { t } = useTranslation('recipientEditor');

  useEffect(() => {
    Promise.allSettled([
      $transferCurrenciesStore.events.getTransferCurrencies(),
      $countriesStore.events.getCountries(),
      $countryProvincesStore.events.getCountryProvinces(),
    ]);
    return () => {
      $recipientMandatoryFieldsStore.events.clear();
      $recipientCreateStore.events.clear();
    };
  }, []);

  useEffect(() => {
    const bankCountryName = countryOptions.find((option) => option.value === bankCountry);
    const residenceCountryName = countryOptions.find((option) => option.value === companyCountry);
    if (bankCountryName && residenceCountryName && currency) {
      $recipientMandatoryFieldsStore.events.getMandatoryFields({
        ResidenceCountryCode: residenceCountryName.value,
        BankCountryCode: bankCountryName.value,
        Currency: currency,
        Type: TransferRecipientTypeEnum.Business,
      });
    }
  }, [bankCountry, companyCountry, currency]);

  const onSubmit = async (data: BusinessRecipientFormData) =>
    await $recipientCreateStore.events.createRecipient({
      ...data,
      type: TransferRecipientTypeEnum.Business,
      countryCode: data.companyCountry,
      bankCountryCode: bankCountry,
      bicOrSwiftCode: data.bic,
    });

  const countryOptions = useMemo(
    () => countriesStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode })),
    [countriesStore],
  );
  return (
    <div className='flex w-full flex-col'>
      {recipientStore.loading && <Spinner position />}
      <FormProvider {...formControl}>
        <form id={formId} className='flex flex-col gap-6 bg-white dark:bg-soft-black' noValidate onSubmit={formControl.handleSubmit(onSubmit)}>
          <CompanyFormBlock />
          <AddressFormBlock type='Business' />
          <AccountFormBlock />
        </form>
        <Button
          className='mt-5 max-w-72 px-28'
          color='primary'
          type='submit'
          form={formId}
          disabled={formControl.formState.isSubmitting || recipientMandatoryFieldsStore.loading}
        >
          {t('buttons.addNewRecipient')}
        </Button>
      </FormProvider>
      <ConfirmModal isOpen={!!recipientStore.data} icon={<ConfirmIcon />} title={t('modals.confirm.title')} />
    </div>
  );
};
